import { withSingleSuperAdminRole } from '../../utils/roleHelpers';
import React, { useEffect, useState } from 'react';
import { db, auth } from '../../firebase'; // Adjust path if needed
import { applyOwnerImpersonation } from '../../utils/impersonation';
import { rememberPeople, nameForUserId } from '../../utils/loadCreator';
import { collection, onSnapshot, query, orderBy, limit, doc, Timestamp, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

// How far back the full-history view reaches. Deliberately generous: an audit
// log that quietly stops at an arbitrary point is worse than no audit log, since
// the absence of an entry gets read as "it did not happen". The largest account
// has produced a few hundred entries in total, so this covers everything today
// with room to grow.
const AUDIT_HISTORY_LIMIT = 2000;

// Field names as the people reading this page would say them. The database
// names leak through otherwise — "loadIdDisplay", "driverId", "actualPU" — and
// a dispatcher trying to work out who changed what should not have to learn the
// schema to read their own audit trail.
const FIELD_LABELS = {
  driverId: 'Driver',
  oldDriverId: 'Driver (before)',
  newDriverId: 'Driver (after)',
  truckId: 'Truck',
  oldTruckId: 'Truck (before)',
  newTruckId: 'Truck (after)',
  dispatcherId: 'Dispatcher',
  brokerId: 'Broker',
  parentCompanyId: 'Company',
  parentCompanyName: 'Company',
  companyName: 'Company',
  assignedCompanyName: 'Company',
  loadIdDisplay: 'Load',
  load_id: 'Load',
  amount: 'Price',
  brokerFeeCollection: 'Broker fee',
  factoringFee: 'Factoring fee',
  driverCollectionAmount: 'Driver pay',
  paymentTerms: 'Payment terms',
  paymentMethod: 'Payment method',
  paymentStatus: 'Payment status',
  invoiceStatus: 'Invoice status',
  pickupLocation: 'Pickup',
  deliveryLocation: 'Delivery',
  pickupDateTime: 'Pickup date',
  deliveryDateTime: 'Delivery date',
  actualPU: 'Actual pickup',
  actualDEL: 'Actual delivery',
  status: 'Status',
  hasDispatchDocument: 'Dispatch doc',
  hasGatePass: 'Gate pass',
  pdfAutoAttached: 'PDF attached',
};

// Helper function to format log details for display
const formatLogDetails = (log, allDrivers = [], allTrucks = [], allDispatchers = [], allBrokers = []) => {
  if (!log.details || Object.keys(log.details).length === 0) {
    return <span className="italic text-gray-400">No specific details logged.</span>;
  }

  const details = log.details;

  // Creation gets one line, whatever is stored on the entry.
  //
  // "This record was created" carries no information beyond the fact itself —
  // every field is on the record, one click away, and current rather than a
  // snapshot of a moment. Printing sixty fields buries the entries that do
  // matter: the changes, where the before and after are the whole point.
  //
  // Done at render rather than only at write, because entries already in the
  // database hold the full dump and would otherwise stay unreadable for as long
  // as they are kept.
  const CREATION_ACTIONS = ['add_load', 'DRIVER_CREATED', 'BROKER_CREATED', 'TRUCK_CREATED', 'USER_CREATED'];
  if (CREATION_ACTIONS.includes(log.action)) {
    const what =
      details.loadIdDisplay || details.load_id ||
      details.driverName || details.brokerName ||
      details.unitNumber || details.updatedUserEmail || null;
    const when = log.timestamp?.toDate
      ? log.timestamp.toDate().toLocaleString()
      : null;

    // Newer entries store a yes/no; older ones stored the document arrays
    // themselves, so fall back to those rather than showing "No" for a load
    // that plainly had paperwork attached.
    const hasList = (v) => Array.isArray(v) && v.length > 0;
    const dispatchDoc = details.hasDispatchDocument ?? (hasList(details.dispatchDocuments) || !!details.pdfAutoAttached);
    const gatePass = details.hasGatePass ?? hasList(details.gatePassDocuments);
    const price = details.amount;

    const Tag = ({ on, label }) => (
      <span className={on ? 'text-green-700' : 'text-gray-400'}>
        {label}: {on ? 'Yes' : 'No'}
      </span>
    );

    return (
      <span className="text-gray-700 text-xs flex flex-wrap items-center gap-x-3 gap-y-0.5">
        {what && <span className="font-semibold text-sm">{what}</span>}
        {(price !== undefined && price !== null && price !== '') && (
          <span className="font-medium text-green-700">${Number(price).toLocaleString()}</span>
        )}
        {log.action === 'add_load' && <Tag on={!!dispatchDoc} label="Dispatch doc" />}
        {log.action === 'add_load' && <Tag on={!!gatePass} label="Gate pass" />}
        {when && <span className="text-gray-400">created {when}</span>}
      </span>
    );
  }

  let detailLines = [];

  // UPDATED: Show tenant info if available
  if (details.tenantId && log.tenantId !== details.tenantId) {
    detailLines.push(<div key="tenant" className="font-semibold text-blue-600">Tenant: {details.tenantId}</div>);
  }

  if (details.loadIdDisplay) {
    detailLines.push(<div key="loadId" className="font-semibold">Load: {details.loadIdDisplay}</div>);
  } else if (details.updatedUserEmail || details.invitedUserEmail || details.disabledUserEmail || details.toggledUserEmail || details.updatedUserName) {
    detailLines.push(<div key="userId" className="font-semibold">User: {details.updatedUserEmail || details.invitedUserEmail || details.disabledUserEmail || details.toggledUserEmail || details.updatedUserName}</div>);
  } else if (details.companyName || details.deletedCompanyName) {
    detailLines.push(<div key="companyId" className="font-semibold">Company: {details.companyName || details.deletedCompanyName}</div>);
  } else if (details.typeName || details.deletedTypeName) {
    detailLines.push(<div key="adjTypeId" className="font-semibold">Adjustment Type: {details.typeName || details.deletedTypeName}</div>);
  } else if (details.driverName) {
    detailLines.push(<div key="driverNameHeader" className="font-semibold">Driver: {details.driverName}</div>);
  } else if (details.unitNumber) {
    detailLines.push(<div key="truckNumHeader" className="font-semibold">Truck: {details.unitNumber}</div>);
  } else if (details.brokerName) {
    detailLines.push(<div key="brokerNameHeader" className="font-semibold">Broker: {details.brokerName}</div>);
  }

  // Helper to format date values
  const formatDateValue = (value) => {
    if (!value) return 'N/A';
    try {
      const date = new Date(value);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return value;
    }
  };

  switch (log.action) {
    // NEW: Handle actual pickup date changes
    case "ACTUAL_PICKUP_DATE_CHANGED":
      detailLines.push(
        <div key="actualPU" className="bg-orange-50 border border-orange-200 rounded p-2 mt-1">
          <div className="font-semibold text-orange-700 mb-1">⚠️ Manual Actual Pickup Date Change</div>
          <div className="text-xs space-y-1">
            <div>Previous: <span className="font-medium">{formatDateValue(details.oldValue)}</span></div>
            <div>New: <span className="font-medium text-blue-700">{formatDateValue(details.newValue)}</span></div>
            <div className="text-gray-500 mt-1">Confirmed by: {details.confirmedBy}</div>
          </div>
        </div>
      );
      break;

    // NEW: Handle actual delivery date changes
    case "ACTUAL_DELIVERY_DATE_CHANGED":
      detailLines.push(
        <div key="actualDEL" className="bg-orange-50 border border-orange-200 rounded p-2 mt-1">
          <div className="font-semibold text-orange-700 mb-1">⚠️ Manual Actual Delivery Date Change</div>
          <div className="text-xs space-y-1">
            <div>Previous: <span className="font-medium">{formatDateValue(details.oldValue)}</span></div>
            <div>New: <span className="font-medium text-blue-700">{formatDateValue(details.newValue)}</span></div>
            <div className="text-gray-500 mt-1">Confirmed by: {details.confirmedBy}</div>
          </div>
        </div>
      );
      break;

    case "LOAD_CREATED":
      if (details.broker) detailLines.push(<div key="broker">Broker: {details.broker}</div>);
      if (typeof details.amount !== 'undefined') detailLines.push(<div key="amount">Amount: ${details.amount.toLocaleString()}</div>);
      break;
    case "LOAD_UPDATED":
      if (details.changes && Object.keys(details.changes).length > 0) {
        detailLines.push(<div key="changesTitle" className="font-medium mt-1">Changes:</div>);
        for (const field in details.changes) {
          let oldValueDisplay = details.changes[field].oldValue;
          let newValueDisplay = details.changes[field].newValue;

          if (field === "driverId") {
            oldValueDisplay = allDrivers.find(d => d.id === oldValueDisplay)?.name || oldValueDisplay || "N/A";
            newValueDisplay = allDrivers.find(d => d.id === newValueDisplay)?.name || newValueDisplay || "N/A";
          } else if (field === "truckId") {
            oldValueDisplay = allTrucks.find(t => t.id === oldValueDisplay)?.unitNumber || oldValueDisplay || "N/A";
            newValueDisplay = allTrucks.find(t => t.id === newValueDisplay)?.unitNumber || newValueDisplay || "N/A";
          } else if (field === "dispatcherId") {
            oldValueDisplay = allDispatchers.find(d => d.id === oldValueDisplay)?.name || oldValueDisplay || "N/A";
            newValueDisplay = allDispatchers.find(d => d.id === newValueDisplay)?.name || newValueDisplay || "N/A";
          }

          oldValueDisplay = (oldValueDisplay === null || oldValueDisplay === undefined || oldValueDisplay === "") ? "[empty]" : String(oldValueDisplay);
          newValueDisplay = (newValueDisplay === null || newValueDisplay === undefined || newValueDisplay === "") ? "[empty]" : String(newValueDisplay);

          detailLines.push(<div key={field} className="ml-2">{`- ${field}: "${oldValueDisplay}" → "${newValueDisplay}"`}</div>);
        }
      } else {
        detailLines.push(<div key="noChanges" className="italic">Saved, but no specific field changes were logged in 'changes' object.</div>);
      }
      break;
    case "LOAD_STATUS_CHANGED":
      if(details.loadIdDisplay && details.loadIdDisplay !== log.targetId) detailLines.push(<div key="statusLoadId">Load ID: {details.loadIdDisplay}</div>);
      detailLines.push(<div key="statusChange">{`Status: "${details.oldStatus || 'N/A'}" → "${details.newStatus || 'N/A'}"`}</div>);
      break;
    case "LOAD_DRIVER_CHANGED":
      if(details.loadIdDisplay && details.loadIdDisplay !== log.targetId) detailLines.push(<div key="driverLoadId">Load ID: {details.loadIdDisplay}</div>);
      const oldDriverName = allDrivers.find(d => d.id === details.oldDriverId)?.name || details.oldDriverId || "N/A";
      const newDriverName = allDrivers.find(d => d.id === details.newDriverId)?.name || details.newDriverId || "N/A";
      detailLines.push(<div key="driverChange">{`Driver: "${oldDriverName}" → "${newDriverName}"`}</div>);
      break;
    case "LOAD_TRUCK_CHANGED":
      if(details.loadIdDisplay && details.loadIdDisplay !== log.targetId) detailLines.push(<div key="truckLoadId">Load ID: {details.loadIdDisplay}</div>);
      const oldTruckUnit = allTrucks.find(t => t.id === details.oldTruckId)?.unitNumber || details.oldTruckId || "N/A";
      const newTruckUnit = allTrucks.find(t => t.id === details.newTruckId)?.unitNumber || details.newTruckId || "N/A";
      detailLines.push(<div key="truckChange">{`Truck: "${oldTruckUnit}" → "${newTruckUnit}"`}</div>);
      break;
    case "LOAD_DELETED":
      detailLines.push(<div key="delLoadId">Deleted Load: {details.loadIdDisplay || log.targetId}</div>);
      if(details.deletedLoadData) {
         detailLines.push(<div key="delBroker">(Broker: {details.deletedLoadData.brokerName || 'N/A'}, Amount: ${ (details.deletedLoadData.amount || 0).toLocaleString()})</div>);
      }
      break;
    case "USER_UPDATED":
      if (details.changes && Object.keys(details.changes).length > 0) {
        detailLines.push(<div key="userChangesTitle" className="font-medium mt-1">User Changes:</div>);
        for (const field in details.changes) {
          detailLines.push(
            <div key={field} className="ml-2">{`- ${field}: "${details.changes[field].oldValue === undefined ? 'N/A' : details.changes[field].oldValue}" → "${details.changes[field].newValue}"`}</div>
          );
        }
      }
      break;
    case "USER_INVITED":
    case "USER_INVITE_INITIATED":
    case "USER_CREATED_BY_ADMIN": 
      if(details.invitedUserName || details.createdUserName) detailLines.push(<div key="invitedName">Invited: {details.invitedUserName || details.createdUserName} ({details.invitedUserEmail || details.createdUserEmail})</div>);
      if(details.roleAssigned) detailLines.push(<div key="invitedRole">Role: {details.roleAssigned}, Company: {details.companyAssigned}</div>);
      if(details.initialStatus) detailLines.push(<div key="invitedStatus">Initial Status: {details.initialStatus}</div>);
      break;
    case "USER_DISABLED":
      detailLines.push(<div key="userDisabled">Disabled User: {details.disabledUserEmail}</div>);
      break;
    case "USER_STATUS_TOGGLED":
    case "USER_ACTIVATED":
    case "USER_DEACTIVATED":
      detailLines.push(<div key="userStatusToggle">User: {details.toggledUserEmail}, New Status: {details.newStatus}</div>);
      break;
    case "COMPANY_CREATED":
      if (details.usdot) detailLines.push(<div key="companyUsdot">USDOT: {details.usdot}</div>);
      if (details.mcNumber) detailLines.push(<div key="companyMC">MC#: {details.mcNumber}</div>);
      if (details.taxId) detailLines.push(<div key="companyTaxId">Tax ID: {details.taxId}</div>);
      break;
    case "COMPANY_UPDATED":
      if (details.changes) {
        detailLines.push(<div key="companyChangesTitle">Company Changes:</div>);
        for (const field in details.changes) {
          detailLines.push(
            <div key={field} className="ml-2">{`- ${field}: "${details.changes[field].oldValue === undefined ? 'N/A' : details.changes[field].oldValue}" → "${details.changes[field].newValue}"`}</div>
          );
        }
      }
      break;
    case "COMPANY_DELETED":
      detailLines.push(<div key="companyDeleted">Deleted Name: {details.deletedCompanyName}</div>);
      break;
    case "COMPANY_STATUS_TOGGLED":
      detailLines.push(<div key="companyStatusToggle">New Status: {details.newStatus}</div>);
      break;
    case "ADJUSTMENT_TYPE_CREATED":
      detailLines.push(<div key="adjTypeDetails">Nature: {details.adjustmentNature}, Amount: ${details.amount}</div>);
      break;
    case "ADJUSTMENT_TYPE_UPDATED":
      if (details.changes) {
        detailLines.push(<div key="adjChangesTitle">Adj. Type Changes:</div>);
        for (const field in details.changes) {
          detailLines.push(
            <div key={field} className="ml-2">{`- ${field}: "${details.changes[field].oldValue === undefined ? 'N/A' : details.changes[field].oldValue}" → "${details.changes[field].newValue}"`}</div>
          );
        }
      } else if (details.adjustmentNature || typeof details.amount !== 'undefined') {
        detailLines.push(<div key="adjTypeNoChanges">Nature: {details.adjustmentNature}, Amount: ${details.amount}</div>);
      }
      break;
    case "ADJUSTMENT_TYPE_DELETED":
      detailLines.push(<div key="adjTypeDeleted">Deleted Type: {details.deletedTypeName}</div>);
      break;
    case "USER_COMPENSATION_UPDATED":
      detailLines.push(<div key="userCompType">Type: {details.newCompensation?.salaryType}, Value: ${details.newCompensation?.salaryValue}, Bonus: ${details.newCompensation?.bonus}, Rating: {details.newCompensation?.rating}</div>);
      break;
    case "APP_TIMEZONE_SAVED":
        detailLines.push(<div key="appTimezone">New Default Timezone: {details.defaultTimeZone}</div>);
        break;
    case "API_KEY_GENERATED":
        if(details.description) detailLines.push(<div key="apiKeyDesc">Description: {details.description}</div>);
        detailLines.push(<div key="apiKeyPrefix">Key Prefix: {details.keyPrefix}...</div>);
        break;
    case "API_KEY_REVOKED":
    case "API_KEY_DELETED":
        detailLines.push(<div key="apiKeyPrefixRevoked">Key Prefix: {details.keyPrefix}...</div>);
        break;
    default: {
      // Generic formatter for the many action types that don't have a bespoke
      // case above. `details.changes` shows up in two different shapes across
      // the codebase: a per-field {oldValue, newValue} diff (some load/user/
      // company actions), or a flat snapshot of the new record (driver/truck/
      // broker create-update actions). Render each shape appropriately instead
      // of dumping raw JSON.
      const noisyKeys = new Set(['id', 'tenantId', 'createdAt', 'updatedAt', 'driverName', 'unitNumber', 'brokerName']);

      const resolveValue = (key, value) => {
        if (value === null || value === undefined || value === '') return '[empty]';
        if (key === 'driverId' || key === 'oldDriverId' || key === 'newDriverId') {
          return allDrivers.find(d => d.id === value)?.name || String(value);
        }
        if (key === 'truckId' || key === 'assignedTruckId' || key === 'oldTruckId' || key === 'newTruckId') {
          return allTrucks.find(t => t.id === value)?.unitNumber || String(value);
        }
        if (key === 'dispatcherId') {
          return allDispatchers.find(d => d.id === value)?.name || String(value);
        }
        if (key === 'brokerId' || key === 'oldBrokerId' || key === 'newBrokerId') {
          return allBrokers.find(b => b.id === value)?.name || String(value);
        }
        if (key === 'parentCompanyId' || key === 'companyId') {
          // Companies are not fetched here, but the name is usually written
          // alongside the id on the same entry — prefer it over the raw id.
          return details.parentCompanyName || details.companyName || String(value);
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') {
          if (typeof value.toDate === 'function') return formatDateValue(value.toDate());
          return JSON.stringify(value);
        }
        return String(value);
      };

      const changes = details.changes;
      if (changes && typeof changes === 'object' && Object.keys(changes).length > 0) {
        const isDiffShape = Object.values(changes).every(
          v => v && typeof v === 'object' && !Array.isArray(v) &&
               Object.keys(v).every(k => k === 'oldValue' || k === 'newValue')
        );

        detailLines.push(<div key="changesTitle" className="font-medium mt-1">{isDiffShape ? 'Changes:' : 'Saved data:'}</div>);
        for (const field in changes) {
          if (isDiffShape) {
            const oldVal = resolveValue(field, changes[field].oldValue);
            const newVal = resolveValue(field, changes[field].newValue);
            detailLines.push(<div key={field} className="ml-2">{`- ${field}: "${oldVal}" → "${newVal}"`}</div>);
          } else {
            if (noisyKeys.has(field)) continue;
            detailLines.push(<div key={field} className="ml-2">{`- ${field}: ${resolveValue(field, changes[field])}`}</div>);
          }
        }
      }

      // Several actions record a change as three flat fields rather than a
      // changes map: field: "driverId", oldValue: "<id>", newValue: "<id>".
      // resolveValue works off the key, so for those it was handed "oldValue"
      // and had nothing to match on — which is why a driver change read as two
      // raw document ids. Render it as one readable line instead, resolving
      // through the field it names.
      if (details.field && ('oldValue' in details || 'newValue' in details)) {
        const label = FIELD_LABELS[details.field] || details.field;
        const before = resolveValue(details.field, details.oldValue);
        const after = resolveValue(details.field, details.newValue);
        detailLines.push(
          <div key="flatChange" className="ml-2">
            <span className="font-medium">{label}:</span>{' '}
            <span className="text-gray-600">{before}</span>
            <span className="text-gray-400"> → </span>
            <span className="font-medium text-blue-700">{after}</span>
          </div>
        );
      }

      // Any other top-level detail fields outside `changes`
      const handledFlat = details.field && ('oldValue' in details || 'newValue' in details)
        ? new Set(['field', 'oldValue', 'newValue'])
        : new Set();

      for (const key in details) {
        if (key === 'tenantId' || key === 'changes' || noisyKeys.has(key)) continue;
        if (handledFlat.has(key)) continue;
        // Ids nobody can read, already shown by name on the line above.
        if (key === 'parentCompanyId' && details.parentCompanyName) continue;
        detailLines.push(
          <div key={key} className="ml-2 text-gray-600">
            {`${FIELD_LABELS[key] || key}: ${resolveValue(key, details[key])}`}
          </div>
        );
      }
      break;
    }
  }

  if (detailLines.length === 0 && log.action) {
     return <span className="italic text-gray-400">Details logged but not formatted for action: {log.action}.</span>;
  }
  if (detailLines.length === 0) {
    return <span className="italic text-gray-400">No specific details formatted.</span>;
  }

  return (
    <div className="text-xs">
      {detailLines}
    </div>
  );
};

function get24HoursAgoTimestamp() {
  const now = new Date();
  return Timestamp.fromDate(new Date(now.getTime() - 24 * 60 * 60 * 1000));
}
// Helper to get a human-readable target identifier
const getDisplayTargetId = (log) => {
  if (log.targetType === 'Load' || log.targetType === 'load') {
    return log.details?.loadIdDisplay || log.targetId;
  }
  if (log.targetType === 'User' || log.targetType === 'user') {
    return log.details?.updatedUserEmail || log.details?.invitedUserEmail || log.details?.disabledUserEmail || log.details?.toggledUserEmail || log.targetId;
  }
  if (log.targetType === 'Company' || log.targetType === 'company') {
    return log.details?.companyName || log.details?.deletedCompanyName || log.targetId;
  }
  if (log.targetType === 'AdjustmentType') {
    return log.details?.typeName || log.details?.deletedTypeName || log.targetId;
  }
  return log.targetId;
};

// Helper to get action badge color
const getActionBadgeColor = (action) => {
  if (action.includes('ACTUAL_PICKUP_DATE_CHANGED') || action.includes('ACTUAL_DELIVERY_DATE_CHANGED')) {
    return 'bg-orange-100 text-orange-800 border-orange-300';
  }
  if (action.includes('CREATED')) return 'bg-green-100 text-green-800 border-green-300';
  if (action.includes('DELETED')) return 'bg-red-100 text-red-800 border-red-300';
  if (action.includes('UPDATED') || action.includes('CHANGED')) return 'bg-blue-100 text-blue-800 border-blue-300';
  return 'bg-gray-100 text-gray-800 border-gray-300';
};

export default function AuditLogPage({ tenantId: propTenantId }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [currentTenantId, setCurrentTenantId] = useState(propTenantId);
  // Opens on the full history, not the last 24 hours.
  //
  // The 24-hour default made sense for an account generating hundreds of entries
  // a day, but on a quieter tenant it just shows an empty page — and an empty
  // audit page reads as "nothing is being recorded" rather than "nothing
  // happened today". Several accounts had dozens of entries and were seeing
  // nothing. The time filter is still here, as a choice rather than the default.
  const [showArchive, setShowArchive] = useState(true);
  const [allDrivers, setAllDrivers] = useState([]); 
  const [allTrucks, setAllTrucks] = useState([]);   
  const [allDispatchers, setAllDispatchers] = useState([]);
  const [allBrokers, setAllBrokers] = useState([]);
  const [permissionError, setPermissionError] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState('current');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // UPDATED: Get current logged-in user profile and extract tenant
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const unsubProfile = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const userData = applyOwnerImpersonation({ uid: user.uid, email: user.email, ...docSnap.data() });
            setLoggedInUser(withSingleSuperAdminRole(userData));
            
            // UPDATED: Set tenant ID from user data if not provided as prop
            if (!propTenantId && userData.tenantId) {
              setCurrentTenantId(userData.tenantId);
            } else if (!propTenantId && userData.assignedCompanyId) {
              setCurrentTenantId(userData.assignedCompanyId);
            } else if (!propTenantId && userData.assignedCompanyName) {
              setCurrentTenantId(`tenant_${userData.assignedCompanyName.toLowerCase().replace(/\s+/g, '_')}`);
            }
          } else {
            console.warn("AuditLogPage: User profile not found, cannot verify permissions.");
            setLoggedInUser({ uid: user.uid, email: user.email, role: null });
            setPermissionError("User profile not found. Cannot verify permissions.");
          }
        });
        return () => unsubProfile();
      } else {
        setLoggedInUser(null);
        setCurrentTenantId(null);
        setIsLoading(false); 
      }
    });
    return unsubscribe;
  }, [propTenantId]);

  // UPDATED: Fetch drivers, trucks, and dispatchers with tenant filtering
  useEffect(() => {
    if (!loggedInUser || !["Super Admin", "Admin"].includes(loggedInUser.role)) {
        setAllDrivers([]);
        setAllTrucks([]);
        setAllDispatchers([]);
        if (loggedInUser) {
            setPermissionError("You do not have permission to view the audit log.");
        }
        setIsLoading(false);
        return;
    }

    if (!currentTenantId) {
      console.warn("AuditLogPage: No tenant ID available, skipping reference data fetch");
      setAllDrivers([]);
      setAllTrucks([]);
      setAllDispatchers([]);
      return;
    }

    setPermissionError(null);

    // UPDATED: Create tenant-aware queries for reference data
    const driversQuery = loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant"
      ? query(collection(db, "drivers"), orderBy("name", "asc"))
      : query(collection(db, "drivers"), where("tenantId", "==", currentTenantId), orderBy("name", "asc"));

    const trucksQuery = loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant"
      ? query(collection(db, "trucks"), orderBy("unitNumber", "asc"))
      : query(collection(db, "trucks"), where("tenantId", "==", currentTenantId), orderBy("unitNumber", "asc"));

    // Roles are stored as an array on current accounts and a bare string on
    // older ones. `where("role","==","Dispatcher")` only ever matched the string
    // form, so on any modern account this returned nothing and the dispatcher
    // names never resolved — entries showed an id instead of a person.
    //
    // Fetching the tenant's users and filtering here handles both shapes. The
    // alternative, array-contains, would miss the legacy string rows.
    const dispatchersQuery = loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant"
      ? query(collection(db, "users"))
      : query(collection(db, "users"), where("tenantId", "==", currentTenantId));

    const unsubDrivers = onSnapshot(driversQuery, (snapshot) => {
      setAllDrivers(snapshot.docs.map(d => ({ id: d.id, name: d.data().name, tenantId: d.data().tenantId })));
    }, err => { 
      console.error("Error fetching drivers for audit log:", err); 
      setError("Could not load driver names for details."); 
    });

    const unsubTrucks = onSnapshot(trucksQuery, (snapshot) => {
      setAllTrucks(snapshot.docs.map(t => ({ id: t.id, unitNumber: t.data().unitNumber, tenantId: t.data().tenantId })));
    }, err => { 
      console.error("Error fetching trucks for audit log:", err); 
      setError("Could not load truck numbers for details."); 
    });

    const unsubDispatchers = onSnapshot(dispatchersQuery, (snapshot) => {
      // Everyone, so each entry can name the person and not just their email.
      rememberPeople(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setAllDispatchers(snapshot.docs
        .filter(d => {
          const role = d.data().role;
          const roles = Array.isArray(role) ? role : (role ? [role] : []);
          return roles.includes('Dispatcher');
        })
        .map(d => ({
          id: d.id,
          name: d.data().name || d.data().email,
          tenantId: d.data().tenantId
        })));
    }, err => {
      console.error("Error fetching dispatchers for audit log:", err); 
      setError("Could not load dispatcher names for details."); 
    });

    // Brokers too: a broker change records two document ids, and without this
    // the entry reads "70usvHMJ1cvIJ3oUHYil → ..." which tells nobody anything.
    const brokersQuery = loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant"
      ? query(collection(db, "brokers"))
      : query(collection(db, "brokers"), where("tenantId", "==", currentTenantId));

    const unsubBrokers = onSnapshot(brokersQuery, (snapshot) => {
      setAllBrokers(snapshot.docs.map(d => ({ id: d.id, name: d.data().name })));
    }, err => {
      // A missing broker name costs a readable line, not the page.
      console.error("Error fetching brokers for audit log:", err);
    });

    return () => { unsubDrivers(); unsubTrucks(); unsubDispatchers(); unsubBrokers(); };
  }, [loggedInUser, currentTenantId]);

  // UPDATED: Fetch audit logs with tenant filtering
  useEffect(() => {
    if (!loggedInUser || permissionError) {
        setLogs([]);
        if (!loggedInUser && !auth.currentUser) setIsLoading(false);
        else if (permissionError) setIsLoading(false);
        return;
    }

    if (!currentTenantId) {
      console.warn("AuditLogPage: No tenant ID available, skipping audit log fetch");
      setLogs([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);

    let q;
    const timestamp24HoursAgo = get24HoursAgoTimestamp();

    // UPDATED: Create tenant-aware audit log queries
    if (loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant") {
      // Super admin can see all logs or filter by specific tenant
      if (selectedTenant === 'all') {
        // Show all logs across all tenants
        if (!showArchive) {
          q = query(
            collection(db, "auditLogs"),
            where("timestamp", ">=", timestamp24HoursAgo),
            orderBy("timestamp", "desc")
          );
        } else {
          q = query(
            collection(db, "auditLogs"),
            orderBy("timestamp", "desc"),
            limit(AUDIT_HISTORY_LIMIT)
          );
        }
      } else if (selectedTenant === 'current') {
        // Show logs for super admin's current tenant
        if (!showArchive) {
          q = query(
            collection(db, "auditLogs"),
            where("tenantId", "==", currentTenantId),
            where("timestamp", ">=", timestamp24HoursAgo),
            orderBy("timestamp", "desc")
          );
        } else {
          q = query(
            collection(db, "auditLogs"),
            where("tenantId", "==", currentTenantId),
            orderBy("timestamp", "desc"),
            limit(AUDIT_HISTORY_LIMIT)
          );
        }
      } else {
        // Show logs for specific selected tenant
        if (!showArchive) {
          q = query(
            collection(db, "auditLogs"),
            where("tenantId", "==", selectedTenant),
            where("timestamp", ">=", timestamp24HoursAgo),
            orderBy("timestamp", "desc")
          );
        } else {
          q = query(
            collection(db, "auditLogs"),
            where("tenantId", "==", selectedTenant),
            orderBy("timestamp", "desc"),
            limit(AUDIT_HISTORY_LIMIT)
          );
        }
      }
    } else {
      // Regular admin/user - only see their tenant's logs
      if (!showArchive) {
        q = query(
          collection(db, "auditLogs"),
          where("tenantId", "==", currentTenantId),
          where("timestamp", ">=", timestamp24HoursAgo),
          orderBy("timestamp", "desc")
        );
      } else {
        q = query(
          collection(db, "auditLogs"),
          where("tenantId", "==", currentTenantId),
          orderBy("timestamp", "desc"),
          limit(AUDIT_HISTORY_LIMIT)
        );
      }
    }

    const unsubscribeLogs = onSnapshot(q, (snapshot) => {
      const logsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLogs(logsData);
      setIsLoading(false);
    }, (err) => {
      console.error("Error fetching audit logs: ", err);
      if (err.code === 'failed-precondition' && err.message.includes('index')) {
        setError(`Firestore query requires an index. Please create it. Query details: collection 'auditLogs', field 'timestamp' descending, and 'timestamp' >= filter with tenantId filter.`);
      } else {
        setError("Failed to fetch audit logs.");
      }
      setLogs([]);
      setIsLoading(false);
    });
    return unsubscribeLogs;
  }, [loggedInUser, showArchive, permissionError, currentTenantId, selectedTenant]);

  // Filter logs by action type, then by the search box.
  //
  // Searching covers who did it, what they did, and anything written into the
  // entry's details — load ids, driver and broker names, the message. An audit
  // trail nobody can search is a pile of records rather than a way to answer a
  // question, and the question is nearly always "what happened to this load" or
  // "what has this person been doing".
  const byAction = actionFilter === 'all'
    ? logs
    : logs.filter(log => {
        if (actionFilter === 'date_changes') {
          return log.action === 'ACTUAL_PICKUP_DATE_CHANGED' || log.action === 'ACTUAL_DELIVERY_DATE_CHANGED';
        }
        return (log.action || '').includes(actionFilter.toUpperCase());
      });

  const needle = searchTerm.trim().toLowerCase();
  const filteredLogs = !needle
    ? byAction
    : byAction.filter(log => {
        // Details hold ids and names in varying shapes, so the whole object is
        // flattened to text rather than naming fields that differ per action.
        let detailText = '';
        try {
          detailText = JSON.stringify(log.details || {});
        } catch (_e) { /* a detail that will not serialise simply is not searched */ }
        return [
          log.userEmail,
          nameForUserId(log.userId),
          log.action,
          log.targetId,
          log.targetType,
          detailText,
        ].filter(Boolean).join(' ').toLowerCase().includes(needle);
      });

  if (isLoading) { 
    return <div className="p-6 text-center text-gray-500">Loading audit logs...</div>;
  }

  if (permissionError) {
    return (
      <div className="max-w-6xl mx-auto py-4 px-1 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-gray-800">Audit Log</h1>
        <div className="p-6 bg-white rounded-lg shadow">
          <p className="text-red-600 font-semibold">Access Denied.</p>
          <p className="text-sm text-gray-600">{permissionError}</p>
        </div>
      </div>
    );
  }

  if (!loggedInUser){ 
     return <div className="p-6 text-center text-gray-500">Please log in to view audit logs.</div>;
  }

  // UPDATED: Handle missing tenant
  if (!currentTenantId) {
    return (
      <div className="max-w-6xl mx-auto py-4 px-1 sm:px-6 lg:px-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-gray-800">Audit Log</h1>
        <div className="p-6 bg-white rounded-lg shadow">
          <div className="text-red-600 mb-2">Tenant information is missing</div>
          <div className="text-sm text-gray-500">Cannot load audit logs without tenant context</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto py-4 px-1 sm:px-6 lg:px-8">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Audit Log</h1>
          <p className="text-sm text-gray-500 mt-1">Current Tenant: {currentTenantId}</p>
        </div>
        
        {/* UPDATED: Tenant selector for Super Admin */}
        {loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant" && (
          <div className="flex flex-col items-end">
            <label className="text-sm font-medium text-gray-700 mb-1">View Logs For:</label>
            <select 
              value={selectedTenant} 
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="current">Current Tenant ({currentTenantId})</option>
              <option value="all">All Tenants</option>
            </select>
          </div>
        )}
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>}
      
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <button
          onClick={() => setShowArchive((s) => !s)}
          className={`px-4 py-2 rounded-md text-sm font-medium shadow transition
            ${showArchive ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}
          `}
        >
          {showArchive ? "Show Recent Logs (Last 24h)" : "Show Full Archive"}
        </button>
        
        {/* Free-text search across who, what, and the entry's details. */}
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search load ID, person, driver, broker…"
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-xs text-gray-500 hover:text-gray-800 whitespace-nowrap"
            >
              clear
            </button>
          )}
        </div>

        {/* NEW: Action type filter */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Filter:</label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Actions</option>
            <option value="date_changes">📅 Date Changes Only</option>
            <option value="created">Created</option>
            <option value="updated">Updated</option>
            <option value="deleted">Deleted</option>
            <option value="status">Status Changes</option>
          </select>
        </div>
        
        <span className="text-xs text-gray-500">
          {showArchive
            ? `Showing up to 400 most recent logs${selectedTenant === 'all' ? ' across all tenants' : ` for ${selectedTenant === 'current' ? 'current tenant' : 'selected tenant'}`}.`
            : `Showing logs from the last 24 hours${selectedTenant === 'all' ? ' across all tenants' : ` for ${selectedTenant === 'current' ? 'current tenant' : 'selected tenant'}`}.`
          }
          {actionFilter !== 'all' && ` (Filtered: ${filteredLogs.length} of ${logs.length})`}
        </span>
      </div>

      <div className="overflow-x-auto bg-white shadow-lg rounded-lg">
        <table className="min-w-full">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Timestamp</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">Target Type</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">Target ID</th>
              {/* UPDATED: Show Tenant column for Super Admin viewing all tenants */}
              {loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant" && selectedTenant === 'all' && (
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden xl:table-cell">Tenant</th>
              )}
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Details</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLogs.length === 0 && !isLoading ? ( 
              <tr>
                <td colSpan={loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant" && selectedTenant === 'all' ? 7 : 6} className="text-center text-gray-500 py-10">
                  {actionFilter !== 'all' 
                    ? `No audit log entries found matching filter "${actionFilter}".`
                    : selectedTenant === 'all' 
                      ? "No audit log entries found across all tenants matching criteria."
                      : `No audit log entries found for ${selectedTenant === 'current' ? 'current tenant' : 'selected tenant'} matching criteria.`
                  }
                </td>
              </tr>
            ) : (
              filteredLogs.map(log => (
                <tr key={log.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {log.timestamp?.toDate().toLocaleString() || 'N/A'}
                  </td>
                  {/* The person's name with their login email under it. The
                      entry records the account that was logged in; a name makes
                      it obvious when several people share one login. */}
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {nameForUserId(log.userId) ? (
                      <>
                        <div className="font-medium text-gray-800">{nameForUserId(log.userId)}</div>
                        <div className="text-xs text-gray-400">{log.userEmail}</div>
                      </>
                    ) : (log.userEmail || log.userId)}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${getActionBadgeColor(log.action)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">{log.targetType}</td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 truncate max-w-xs hidden lg:table-cell" title={getDisplayTargetId(log)}>{getDisplayTargetId(log)}</td>
                  {/* UPDATED: Show tenant info for Super Admin viewing all tenants */}
                  {loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant" && selectedTenant === 'all' && (
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {log.tenantId || 'No Tenant'}
                      </span>
                    </td>
                  )}
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-500">
                    {formatLogDetails(log, allDrivers, allTrucks, allDispatchers, allBrokers)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* UPDATED: Show tenant filtering info */}
      <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
        <div className="font-medium mb-1">Audit Log Information:</div>
        <div>• Logs are filtered by tenant for security and privacy</div>
        <div>• Current viewing scope: {
          selectedTenant === 'all' ? 'All tenants (Super Admin only)' :
          selectedTenant === 'current' ? `Current tenant (${currentTenantId})` :
          `Selected tenant (${selectedTenant})`
        }</div>
        <div>• Time range: {showArchive ? 'Up to 400 most recent entries' : 'Last 24 hours'}</div>
        <div>• <span className="text-orange-600 font-medium">Orange highlighted actions</span> indicate manual date changes that were confirmed by admin</div>
        {loggedInUser.role === "Super Admin" && currentTenantId === "admin_tenant" && (
          <div>• Super Admin: Use the tenant selector above to view logs from specific tenants</div>
        )}
      </div>
    </div>
  );
}